/**
 * TagFiltersSection — intake tag include/exclude filters (extracted from the
 * old god component's `renderOrderTagFilters`).
 *
 * Dirty model follows StationSettingsSection: local include/exclude tag
 * lists are compared against a saved-server-state ref (not query data
 * directly, so a mid-edit refetch can't yank the baseline out from under the
 * operator); `save` persists via `updateTagFilters` then refreshes the ref.
 */
import { useEffect, useRef, useState } from 'react';
import { Plus, Tag, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { trpc } from '@/lib/trpc';
import { SettingsSection } from '../components/console/SettingsSection';
import { useDirtyRegistration } from '../components/console/SettingsDirtyContext';

interface SavedTagFilters {
  includeTags: string[];
  excludeTags: string[];
}

export function TagFiltersSection() {
  const tagFiltersQuery = trpc.productionSettings.getTagFilters.useQuery();
  const updateTagFiltersMutation = trpc.productionSettings.updateTagFilters.useMutation();

  const [includeTags, setIncludeTags] = useState<string[]>([]);
  const [excludeTags, setExcludeTags] = useState<string[]>([]);
  const [newIncludeTag, setNewIncludeTag] = useState('');
  const [newExcludeTag, setNewExcludeTag] = useState('');
  const [tagFiltersLoaded, setTagFiltersLoaded] = useState(false);
  // Decoupled from tagFiltersQuery.data so a background refetch mid-edit
  // can't move the dirty-check baseline out from under the operator.
  const savedTagFiltersRef = useRef<SavedTagFilters>({ includeTags: [], excludeTags: [] });

  useEffect(() => {
    if (tagFiltersQuery.data && !tagFiltersLoaded) {
      setIncludeTags(tagFiltersQuery.data.includeTags);
      setExcludeTags(tagFiltersQuery.data.excludeTags);
      savedTagFiltersRef.current = {
        includeTags: tagFiltersQuery.data.includeTags,
        excludeTags: tagFiltersQuery.data.excludeTags,
      };
      setTagFiltersLoaded(true);
    }
  }, [tagFiltersQuery.data, tagFiltersLoaded]);

  const saved = savedTagFiltersRef.current;
  const isDirty =
    JSON.stringify(includeTags) !== JSON.stringify(saved.includeTags) ||
    JSON.stringify(excludeTags) !== JSON.stringify(saved.excludeTags);

  useDirtyRegistration(
    'tag-filters',
    isDirty
      ? {
          label: 'Intake tag filters',
          save: async () => {
            try {
              await updateTagFiltersMutation.mutateAsync({ includeTags, excludeTags });
            } catch (error) {
              toast.error(
                `Failed to save: ${error instanceof Error ? error.message : 'Unknown error'}`,
              );
              throw error;
            }
            savedTagFiltersRef.current = { includeTags, excludeTags };
            toast.success('Tag filters saved');
            tagFiltersQuery.refetch();
          },
          discard: () => {
            setIncludeTags(savedTagFiltersRef.current.includeTags);
            setExcludeTags(savedTagFiltersRef.current.excludeTags);
          },
        }
      : null,
  );

  const handleAddIncludeTag = () => {
    const tag = newIncludeTag.trim();
    if (tag && !includeTags.includes(tag) && includeTags.length < 20) {
      setIncludeTags([...includeTags, tag]);
      setNewIncludeTag('');
    }
  };

  const handleAddExcludeTag = () => {
    const tag = newExcludeTag.trim();
    if (tag && !excludeTags.includes(tag) && excludeTags.length < 20) {
      setExcludeTags([...excludeTags, tag]);
      setNewExcludeTag('');
    }
  };

  const handleRemoveIncludeTag = (tagToRemove: string) => {
    setIncludeTags(includeTags.filter((t) => t !== tagToRemove));
  };

  const handleRemoveExcludeTag = (tagToRemove: string) => {
    setExcludeTags(excludeTags.filter((t) => t !== tagToRemove));
  };

  return (
    <SettingsSection
      id="tag-filters"
      title="Intake tag filters"
      description="Include/exclude Shopify tags that gate orders into production."
      icon={Tag}
      scope="org"
    >
      {tagFiltersQuery.isPending ? (
        <div className="flex items-center justify-center py-8">
          <span className="text-sm text-muted-foreground">Loading…</span>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Include Tags */}
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="include-tags" className="text-sm font-medium">
                Include Tags
              </Label>
              <p className="text-xs text-muted-foreground">
                Orders must have at least one of these tags to appear in production. Leave empty
                to include all orders.
              </p>
            </div>
            <div className="flex min-h-[44px] flex-wrap gap-2 rounded-md border bg-muted/30 p-2">
              {includeTags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1.5 rounded-full bg-green-100 py-1 pl-3 pr-1 text-sm font-medium text-green-800"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveIncludeTag(tag)}
                    className="flex min-h-[32px] min-w-[32px] items-center justify-center rounded-full transition-colors hover:bg-green-200"
                    aria-label={`Remove ${tag} tag`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </span>
              ))}
              {includeTags.length === 0 && (
                <span className="text-sm italic text-muted-foreground">
                  No include filters (all orders included)
                </span>
              )}
            </div>
            <div className="flex gap-2">
              <Input
                id="include-tags"
                placeholder="Add tag (e.g., priority, rush)"
                value={newIncludeTag}
                onChange={(e) => setNewIncludeTag(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddIncludeTag();
                  }
                }}
                className="h-10 flex-1"
              />
              <Button
                variant="outline"
                onClick={handleAddIncludeTag}
                disabled={!newIncludeTag.trim() || includeTags.length >= 20}
                className="h-10 gap-1"
              >
                <Plus className="h-4 w-4" />
                Add
              </Button>
            </div>
          </div>

          <Separator />

          {/* Exclude Tags */}
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="exclude-tags" className="text-sm font-medium">
                Exclude Tags
              </Label>
              <p className="text-xs text-muted-foreground">
                Orders with any of these tags will be hidden from production. Useful for filtering
                out wholesale, samples, or test orders.
              </p>
            </div>
            <div className="flex min-h-[44px] flex-wrap gap-2 rounded-md border bg-muted/30 p-2">
              {excludeTags.map((tag) => (
                <span
                  key={tag}
                  className="inline-flex items-center gap-1.5 rounded-full bg-red-100 py-1 pl-3 pr-1 text-sm font-medium text-red-800"
                >
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveExcludeTag(tag)}
                    className="flex min-h-[32px] min-w-[32px] items-center justify-center rounded-full transition-colors hover:bg-red-200"
                    aria-label={`Remove ${tag} tag`}
                  >
                    <X className="h-4 w-4" />
                  </button>
                </span>
              ))}
              {excludeTags.length === 0 && (
                <span className="text-sm italic text-muted-foreground">No exclude filters</span>
              )}
            </div>
            <div className="flex gap-2">
              <Input
                id="exclude-tags"
                placeholder="Add tag (e.g., wholesale, sample)"
                value={newExcludeTag}
                onChange={(e) => setNewExcludeTag(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddExcludeTag();
                  }
                }}
                className="h-10 flex-1"
              />
              <Button
                variant="outline"
                onClick={handleAddExcludeTag}
                disabled={!newExcludeTag.trim() || excludeTags.length >= 20}
                className="h-10 gap-1"
              >
                <Plus className="h-4 w-4" />
                Add
              </Button>
            </div>
          </div>
        </div>
      )}
    </SettingsSection>
  );
}
